import type { Place } from '@wingman/shared';
import { CATEGORIES } from '@wingman/shared';
import { getCategoryIcon } from '../../lib/categoryIcons.js';

export type OpenStatus = 'open' | 'closed' | 'unknown';

// ── Shared parser helpers ──────────────────────────────────────────────────

const DAY_NAMES = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'] as const;
const DAY_INDEX: Record<string, number> = Object.fromEntries(DAY_NAMES.map((d, i) => [d, i]));

function toMinutes(value: string): number | null {
  const [h, m] = value.split(':').map(Number);
  if (!Number.isInteger(h) || !Number.isInteger(m) || h > 24 || m > 59) return null;
  return h * 60 + m;
}

function appliesOnDay(start: number, end: number | undefined, day: number): boolean {
  if (end === undefined) return start === day;
  return start <= end ? day >= start && day <= end : day >= start || day <= end;
}

// ── Public API ─────────────────────────────────────────────────────────────

export function getOpenStatus(place: Place): OpenStatus {
  const hours = place.tags?.opening_hours;
  if (!hours) return 'unknown';
  if (hours === '24/7') return 'open';
  if (hours.toLowerCase() === 'off' || hours.toLowerCase() === 'closed') return 'closed';

  const now = new Date();
  const day = now.getDay();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const rules = hours.split(';').map((r) => r.trim()).filter(Boolean);
  let matchedDay = false;

  for (const rule of rules) {
    const match = rule.match(/^(?:(Mo|Tu|We|Th|Fr|Sa|Su)(?:-(Mo|Tu|We|Th|Fr|Sa|Su))?\s+)?(\d{1,2}:\d{2}-\d{1,2}:\d{2}|off|closed)$/i);
    if (!match) return 'unknown';
    const [, startDay, endDay, schedule] = match;
    const applies = !startDay || appliesOnDay(DAY_INDEX[startDay], endDay ? DAY_INDEX[endDay] : undefined, day);
    if (!applies) continue;
    matchedDay = true;
    if (schedule.toLowerCase() === 'off' || schedule.toLowerCase() === 'closed') return 'closed';
    const [start, end] = schedule.split('-').map(toMinutes);
    if (start === null || end === null) return 'unknown';
    if (start <= end
      ? currentMinutes >= start && currentMinutes < end
      : currentMinutes >= start || currentMinutes < end) return 'open';
  }

  return matchedDay ? 'closed' : 'unknown';
}

/**
 * Returns the minute-of-day at which this place closes today (if currently open),
 * or null if closed/unknown/24h. Used to detect "closing within 60 min".
 */
export function getClosingMinuteToday(place: Place): number | null {
  const hours = place.tags?.opening_hours;
  if (!hours || hours === '24/7') return null;

  const now = new Date();
  const day = now.getDay();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const rules = hours.split(';').map((r) => r.trim()).filter(Boolean);

  for (const rule of rules) {
    const match = rule.match(/^(?:(Mo|Tu|We|Th|Fr|Sa|Su)(?:-(Mo|Tu|We|Th|Fr|Sa|Su))?\s+)?(\d{1,2}:\d{2}-\d{1,2}:\d{2}|off|closed)$/i);
    if (!match) continue;
    const [, startDay, endDay, schedule] = match;
    const applies = !startDay || appliesOnDay(DAY_INDEX[startDay], endDay ? DAY_INDEX[endDay] : undefined, day);
    if (!applies) continue;
    if (schedule.toLowerCase() === 'off' || schedule.toLowerCase() === 'closed') return null;
    const [start, end] = schedule.split('-').map(toMinutes);
    if (start === null || end === null) continue;
    const isOpen = start <= end
      ? currentMinutes >= start && currentMinutes < end
      : currentMinutes >= start || currentMinutes < end;
    return isOpen ? end : null;
  }
  return null;
}

// ── Legacy hover popup (kept for non-cluster markers) ─────────────────────

export function buildPopupHTML(place: Place): string {
  const cat = CATEGORIES.find((c) => c.id === place.categoryId);
  const Icon = getCategoryIcon(cat?.slug || '');
  const status = getOpenStatus(place);

  const iconSvg = Icon.toString().replace('width="24"', 'width="14"').replace('height="24"', 'height="14"');
  const statusLabel = status === 'open' ? 'OPEN' : status === 'closed' ? 'CLOSED' : '—';
  const statusClass = `wingman-popup-status wingman-popup-status-${status}`;

  const ratingHtml = place.rating !== undefined && place.rating > 0
    ? `<div class="wingman-popup-rating"><svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg> ${place.rating.toFixed(1)}</div>`
    : '';

  const categoryHtml = place.categoryName
    ? `<span class="wingman-popup-category">${escapeHtml(place.categoryName)}</span>`
    : '';

  return `
    <div class="wingman-popup">
      <div class="wingman-popup-header">
        <div class="wingman-popup-icon" style="background: ${cat?.color || '#00f5ff'}22; color: ${cat?.color || '#00f5ff'};">
          ${iconSvg}
        </div>
        <div class="wingman-popup-name">${escapeHtml(place.name)}</div>
      </div>
      <div class="wingman-popup-meta">
        ${categoryHtml}
        ${ratingHtml}
      </div>
      <div class="${statusClass}">
        <span style="width:6px;height:6px;border-radius:50%;background:currentColor;display:inline-block;"></span>
        ${statusLabel}
      </div>
      <div class="wingman-popup-hint">Click for details</div>
    </div>
  `;
}

function escapeHtml(str: string): string {
  return str.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
